import type {
  BBox,
  DisplacementComponent,
  DisplacementResponse,
  InterferogramResponse,
  LocalAOI,
  PolygonTimeSeriesResponse,
  RadarPointsResponse,
  StoredProduct,
} from '../types';

export interface StaticAoi extends LocalAOI {
  static_product_id?: string | null;
  product_error?: string | null;
}

export interface StaticProductEntry {
  product: StoredProduct;
  manifest_file: string;
  radar_points_file: string;
  point_count: number;
}

export interface StaticCatalog {
  schema: string;
  generated_at: string;
  aois: StaticAoi[];
  products: Record<string, StaticProductEntry>;
}

interface StaticProductManifest {
  local_id: string;
  displacement: DisplacementResponse;
  displacements?: Partial<Record<DisplacementComponent, DisplacementResponse>>;
  raw_rasters?: Partial<Record<DisplacementComponent, StaticRawRaster>>;
  temporal_rasters?: Partial<Record<DisplacementComponent, StaticTemporalRaster[]>>;
  interferogram?: InterferogramResponse | null;
  radar_points_file: string;
  point_count: number;
}

interface StaticRawRaster {
  file: string;
  dtype: 'float32-le';
  unit: 'cm/yr';
  component: DisplacementComponent;
  width: number;
  height: number;
  bbox: BBox;
  min: number;
  max: number;
  native_resolution_m: number;
  nodata: 'NaN';
}

interface StaticTemporalRaster extends Omit<StaticRawRaster, 'unit'> {
  unit: 'cm';
  start_date: string;
  end_date: string;
}

let catalogPromise: Promise<StaticCatalog | null> | null = null;
const productManifestCache = new Map<string, Promise<StaticProductManifest>>();
const pointsCache = new Map<string, Promise<RadarPointsResponse>>();
const rawRasterCache = new Map<string, Promise<Float32Array>>();

function staticUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
  const clean = path.replace(/^\.\//, '').replace(/^\//, '');
  return `${base}/${clean}`.replace(/\/{2,}/g, '/');
}

export function loadStaticCatalog(force = false): Promise<StaticCatalog | null> {
  if (force) {
    catalogPromise = null;
    productManifestCache.clear();
    pointsCache.clear();
    rawRasterCache.clear();
  }
  if (!catalogPromise) {
    const suffix = force ? `?v=${Date.now()}` : '';
    catalogPromise = fetch(`${staticUrl('static-data/catalog.json')}${suffix}`, { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null);
  }
  return catalogPromise;
}

export function staticStoredProducts(catalog: StaticCatalog): StoredProduct[] {
  return Object.values(catalog.products)
    .map((entry) => entry.product)
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
}

export async function renderStaticProduct(
  catalog: StaticCatalog,
  productId: string,
  component?: DisplacementComponent,
  scale?: {
    threshold_cm_yr: number;
    color_max_cm_yr: number;
    filter_below_threshold: boolean;
    start_date?: string;
    end_date?: string;
  },
): Promise<DisplacementResponse> {
  const manifest = await loadProductManifest(catalog, productId);
  const requested = component ?? manifest.displacement.component;
  const displacement = manifest.displacements?.[requested] ?? (
    manifest.displacement.component === requested ? manifest.displacement : null
  );
  if (!displacement) {
    throw new Error(`Este producto estatico no incluye componente ${requested}.`);
  }
  const resolved: DisplacementResponse = {
    ...displacement,
    overlay: {
      ...displacement.overlay,
      url: staticUrl(displacement.overlay.url),
    },
  };
  if (!scale) return resolved;

  const temporal = manifest.temporal_rasters?.[requested] ?? [];
  if (scale.start_date && scale.end_date && temporal.length > 0) {
    const temporalOverlay = await recolorTemporalRasters(catalog, productId, temporal, resolved.overlay, scale);
    if (temporalOverlay) {
      const days = daysBetween(scale.start_date, scale.end_date);
      return {
        ...resolved,
        mode: `${resolved.mode} · ${scale.start_date} -> ${scale.end_date}`,
        overlay: temporalOverlay,
        reference_date: scale.end_date,
        caveats: [
          ...resolved.caveats.filter((note) => !/^Display threshold:/i.test(note)),
          `Offline temporal window: ${scale.start_date} to ${scale.end_date}; selected pair rasters were summed and annualized over ${days} days.`,
        ],
      };
    }
  }

  const raw = manifest.raw_rasters?.[requested];
  if (!raw) return resolved;
  const overlay = await recolorRawRaster(
    productId,
    resolveManifestRelative(catalog, productId, raw.file),
    raw,
    resolved.overlay,
    scale,
  );
  return {
    ...resolved,
    overlay,
    caveats: [
      ...resolved.caveats.filter((note) => !/^Display threshold:/i.test(note)),
      `Display threshold: ${scale.threshold_cm_yr.toFixed(2)} cm/yr; color maximum: ${scale.color_max_cm_yr.toFixed(2)} cm/yr.`,
      ...(scale.start_date && scale.end_date && temporal.length === 0
        ? [`This static product has no temporal rasters for ${requested}; date filtering falls back to the full-period raw rate raster.`]
        : []),
      'Static frontend recolor: generated in-browser from the raw rate raster included in the static product.',
    ],
  };
}

export async function renderStaticInterferogram(catalog: StaticCatalog, productId: string): Promise<InterferogramResponse> {
  const manifest = await loadProductManifest(catalog, productId);
  if (!manifest.interferogram) {
    throw new Error('Este producto estatico no tiene interferograma guardado.');
  }
  return {
    ...manifest.interferogram,
    overlay: {
      ...manifest.interferogram.overlay,
      url: staticUrl(manifest.interferogram.overlay.url),
    },
  };
}

export async function staticRadarPoints(catalog: StaticCatalog, productId: string): Promise<RadarPointsResponse> {
  if (!pointsCache.has(productId)) {
    pointsCache.set(productId, loadProductManifest(catalog, productId).then((manifest) => (
      fetch(staticUrl(manifest.radar_points_file)).then((res) => {
        if (!res.ok) throw new Error(`No se pudieron cargar los puntos estaticos (${res.status}).`);
        return res.json() as Promise<RadarPointsResponse>;
      })
    )));
  }
  return pointsCache.get(productId)!;
}

export async function analyzeStaticPolygon(
  catalog: StaticCatalog,
  productId: string,
  coordinates: [number, number][],
): Promise<PolygonTimeSeriesResponse> {
  const manifest = await loadProductManifest(catalog, productId);
  const pointsPayload = await staticRadarPoints(catalog, productId);
  const points = pointsPayload.points.filter((p) => pointInRing([p.lon, p.lat], coordinates));
  const velocities = points.map((p) => Number(p.mean_velocity_cm_yr)).filter(Number.isFinite);
  const meanVelocity = velocities.length
    ? velocities.reduce((sum, value) => sum + value, 0) / velocities.length
    : 0;
  const variance = velocities.length
    ? velocities.reduce((sum, value) => sum + (value - meanVelocity) ** 2, 0) / velocities.length
    : 0;
  const stdVelocity = Math.sqrt(variance);
  const component = manifest.displacement.component as DisplacementComponent;
  const temporal = manifest.temporal_rasters?.[component] ?? [];
  const temporalSamples = temporal.length
    ? await polygonTemporalSamples(catalog, productId, coordinates, temporal, points.length)
    : [];
  const finalTemporalSample = temporalSamples.length ? temporalSamples[temporalSamples.length - 1] : null;
  const startDate = temporalSamples[0]?.date
    ?? manifest.displacement.reference_date
    ?? manifest.displacement.overlay.generated_at.slice(0, 10);
  const endDate = finalTemporalSample?.date
    ?? manifest.displacement.reference_date
    ?? startDate;
  const years = Math.max(1 / 365.25, daysBetween(startDate, endDate) / 365.25);
  const finalMean = finalTemporalSample?.mean_displacement_cm ?? meanVelocity * years;
  const finalStd = finalTemporalSample?.std_displacement_cm ?? stdVelocity * years;
  const samples = temporalSamples.length > 1
    ? temporalSamples
    : [
        {
          date: startDate,
          mean_displacement_cm: 0,
          std_displacement_cm: 0,
          n_points: points.length,
          min_displacement_cm: 0,
          max_displacement_cm: 0,
        },
        {
          date: endDate,
          mean_displacement_cm: Number(finalMean.toFixed(4)),
          std_displacement_cm: Number(finalStd.toFixed(4)),
          n_points: points.length,
          min_displacement_cm: Number((finalMean - finalStd).toFixed(4)),
          max_displacement_cm: Number((finalMean + finalStd).toFixed(4)),
        },
      ];

  return {
    component,
    unit: 'cm',
    polygon_area_km2: polygonAreaKm2(coordinates),
    n_points: points.length,
    mean_velocity_cm_yr: Number((finalMean / years).toFixed(3)),
    start_date: startDate,
    end_date: endDate,
    samples,
    points: points.slice(0, 12000),
  };
}

async function polygonTemporalSamples(
  catalog: StaticCatalog,
  productId: string,
  ring: [number, number][],
  rasters: StaticTemporalRaster[],
  pointCount: number,
) {
  const sorted = [...rasters].sort((a, b) => a.end_date.localeCompare(b.end_date));
  const first = sorted[0];
  if (!first) return [];

  let cumulativeMean = 0;
  let cumulativeVariance = 0;
  const samples = [{
    date: sorted[0].start_date,
    mean_displacement_cm: 0,
    std_displacement_cm: 0,
    n_points: pointCount,
    min_displacement_cm: 0,
    max_displacement_cm: 0,
  }];

  for (const item of sorted) {
    const values = await loadRawRaster(productId, resolveManifestRelative(catalog, productId, item.file), item);
    const indices = rasterCellIndicesInRing(item, ring);
    const stats = rasterStatsAtIndices(values, indices);
    if (!stats) continue;
    cumulativeMean += stats.mean;
    cumulativeVariance += stats.std ** 2;
    const cumulativeStd = Math.sqrt(cumulativeVariance);
    samples.push({
      date: item.end_date,
      mean_displacement_cm: Number(cumulativeMean.toFixed(4)),
      std_displacement_cm: Number(cumulativeStd.toFixed(4)),
      n_points: pointCount,
      min_displacement_cm: Number((cumulativeMean - cumulativeStd).toFixed(4)),
      max_displacement_cm: Number((cumulativeMean + cumulativeStd).toFixed(4)),
    });
  }
  return samples;
}

function loadProductManifest(catalog: StaticCatalog, productId: string): Promise<StaticProductManifest> {
  const entry = catalog.products[productId];
  if (!entry) throw new Error('Producto estatico no encontrado.');
  if (!productManifestCache.has(productId)) {
    productManifestCache.set(productId, fetch(staticUrl(entry.manifest_file), { cache: 'no-store' }).then((res) => {
      if (!res.ok) throw new Error(`No se pudo cargar el producto estatico (${res.status}).`);
      return res.json() as Promise<StaticProductManifest>;
    }));
  }
  return productManifestCache.get(productId)!;
}

function pointInRing(point: [number, number], ring: [number, number][]): boolean {
  const [x, y] = point;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const intersects = ((yi > y) !== (yj > y))
      && (x < ((xj - xi) * (y - yi)) / ((yj - yi) || Number.EPSILON) + xi);
    if (intersects) inside = !inside;
  }
  return inside;
}

function ringBBox(ring: [number, number][]): BBox {
  const lons = ring.map((p) => p[0]);
  const lats = ring.map((p) => p[1]);
  return [Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats)];
}

function polygonAreaKm2(ring: [number, number][]): number {
  if (ring.length < 3) return 0;
  const bbox = ringBBox(ring);
  const meanLat = ((bbox[1] + bbox[3]) / 2) * Math.PI / 180;
  const kmPerLon = 111.32 * Math.cos(meanLat);
  const kmPerLat = 110.57;
  let area = 0;
  for (let i = 0; i < ring.length; i += 1) {
    const j = (i + 1) % ring.length;
    const xi = ring[i][0] * kmPerLon;
    const yi = ring[i][1] * kmPerLat;
    const xj = ring[j][0] * kmPerLon;
    const yj = ring[j][1] * kmPerLat;
    area += xi * yj - xj * yi;
  }
  return Number((Math.abs(area) / 2).toFixed(4));
}

function rasterCellIndicesInRing(meta: { width: number; height: number; bbox: BBox }, ring: [number, number][]): number[] {
  const [minLon, minLat, maxLon, maxLat] = meta.bbox;
  const ringBounds = ringBBox(ring);
  const x0 = clamp(Math.floor(((ringBounds[0] - minLon) / (maxLon - minLon)) * meta.width), 0, meta.width - 1);
  const x1 = clamp(Math.ceil(((ringBounds[2] - minLon) / (maxLon - minLon)) * meta.width), 0, meta.width - 1);
  const y0 = clamp(Math.floor(((maxLat - ringBounds[3]) / (maxLat - minLat)) * meta.height), 0, meta.height - 1);
  const y1 = clamp(Math.ceil(((maxLat - ringBounds[1]) / (maxLat - minLat)) * meta.height), 0, meta.height - 1);
  const indices: number[] = [];
  for (let y = y0; y <= y1; y += 1) {
    const lat = maxLat - ((y + 0.5) / meta.height) * (maxLat - minLat);
    for (let x = x0; x <= x1; x += 1) {
      const lon = minLon + ((x + 0.5) / meta.width) * (maxLon - minLon);
      if (pointInRing([lon, lat], ring)) {
        indices.push(y * meta.width + x);
      }
    }
  }
  return indices;
}

function rasterStatsAtIndices(values: Float32Array, indices: number[]) {
  let n = 0;
  let sum = 0;
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const idx of indices) {
    const value = values[idx];
    if (!Number.isFinite(value)) continue;
    n += 1;
    sum += value;
    min = Math.min(min, value);
    max = Math.max(max, value);
  }
  if (n === 0) return null;
  const mean = sum / n;
  let variance = 0;
  for (const idx of indices) {
    const value = values[idx];
    if (!Number.isFinite(value)) continue;
    variance += (value - mean) ** 2;
  }
  return {
    mean,
    std: Math.sqrt(variance / n),
    min,
    max,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function daysBetween(start: string, end: string): number {
  const a = new Date(`${start}T00:00:00Z`).getTime();
  const b = new Date(`${end}T00:00:00Z`).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 1;
  return Math.max(1, Math.round((b - a) / 86_400_000));
}

async function loadRawRaster(productId: string, fileUrl: string, meta: { width: number; height: number }): Promise<Float32Array> {
  const key = `${productId}:${fileUrl}`;
  if (!rawRasterCache.has(key)) {
    rawRasterCache.set(key, fetch(fileUrl, { cache: 'no-store' }).then(async (res) => {
      if (!res.ok) throw new Error(`No se pudo cargar el raster bruto (${res.status}).`);
      const buffer = await res.arrayBuffer();
      const values = new Float32Array(buffer);
      if (values.length !== meta.width * meta.height) {
        throw new Error(`Raster bruto inválido: ${values.length} valores para ${meta.width}x${meta.height}.`);
      }
      return values;
    }));
  }
  return rawRasterCache.get(key)!;
}

async function recolorTemporalRasters(
  catalog: StaticCatalog,
  productId: string,
  rasters: StaticTemporalRaster[],
  overlay: DisplacementResponse['overlay'],
  scale: {
    threshold_cm_yr: number;
    color_max_cm_yr: number;
    filter_below_threshold: boolean;
    start_date?: string;
    end_date?: string;
  },
) {
  if (!scale.start_date || !scale.end_date) return null;
  const selected = rasters.filter((r) => r.end_date >= scale.start_date! && r.start_date <= scale.end_date!);
  if (selected.length === 0) return null;
  const first = selected[0];
  if (!selected.every((r) => r.width === first.width && r.height === first.height)) return null;
  const total = new Float32Array(first.width * first.height);
  const valid = new Uint16Array(total.length);
  for (const item of selected) {
    const values = await loadRawRaster(productId, resolveManifestRelative(catalog, productId, item.file), item);
    for (let i = 0; i < values.length; i += 1) {
      const value = values[i];
      if (!Number.isFinite(value)) continue;
      total[i] += value;
      valid[i] += 1;
    }
  }
  const years = daysBetween(scale.start_date, scale.end_date) / 365.25;
  for (let i = 0; i < total.length; i += 1) {
    total[i] = valid[i] ? total[i] / Math.max(years, 1 / 365.25) : Number.NaN;
  }
  return drawRateRaster(
    total,
    first.width,
    first.height,
    first.bbox,
    overlay,
    scale,
    `temporal-${scale.start_date}-${scale.end_date}-${selected.length}`,
  );
}

async function recolorRawRaster(
  productId: string,
  fileUrl: string,
  raw: StaticRawRaster,
  overlay: DisplacementResponse['overlay'],
  scale: {
    threshold_cm_yr: number;
    color_max_cm_yr: number;
    filter_below_threshold: boolean;
  },
) {
  const values = await loadRawRaster(productId, fileUrl, raw);
  return drawRateRaster(values, raw.width, raw.height, raw.bbox, overlay, scale, 'raw');
}

function drawRateRaster(
  values: Float32Array,
  width: number,
  height: number,
  bbox: BBox,
  overlay: DisplacementResponse['overlay'],
  scale: {
    threshold_cm_yr: number;
    color_max_cm_yr: number;
    filter_below_threshold: boolean;
  },
  label: string,
) {
  const lowCanvas = document.createElement('canvas');
  lowCanvas.width = width;
  lowCanvas.height = height;
  const lowCtx = lowCanvas.getContext('2d');
  if (!lowCtx) return overlay;
  const threshold = Math.max(0, scale.threshold_cm_yr);
  const maxRate = Math.max(0.1, scale.color_max_cm_yr);
  const image = lowCtx.createImageData(width, height);
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    if (!Number.isFinite(value) || (scale.filter_below_threshold && value < threshold)) continue;
    const [r, g, b] = rateRgb(value, maxRate);
    const offset = i * 4;
    image.data[offset] = r;
    image.data[offset + 1] = g;
    image.data[offset + 2] = b;
    image.data[offset + 3] = value >= threshold ? 170 : 82;
  }
  lowCtx.putImageData(image, 0, 0);
  const outCanvas = document.createElement('canvas');
  outCanvas.width = overlay.width_px;
  outCanvas.height = overlay.height_px;
  const outCtx = outCanvas.getContext('2d');
  if (!outCtx) return overlay;
  outCtx.imageSmoothingEnabled = true;
  outCtx.imageSmoothingQuality = 'high';
  outCtx.drawImage(lowCanvas, 0, 0, outCanvas.width, outCanvas.height);
  return {
    ...overlay,
    id: `${overlay.id}-${label}-scale-${threshold.toFixed(2)}-${maxRate.toFixed(2)}-${scale.filter_below_threshold ? 'hide' : 'show'}`,
    url: outCanvas.toDataURL('image/png'),
    bbox,
    generated_at: new Date().toISOString(),
  };
}

function rateRgb(value: number, maxRate: number): [number, number, number] {
  const stops = ['#22e327', '#77f11e', '#ddf21a', '#ff9d12', '#ef2b19', '#8e0808'];
  const t = Math.max(0, Math.min(1, value / maxRate));
  const scaled = t * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(scaled));
  return mixRgb(stops[i], stops[i + 1], scaled - i);
}

function mixRgb(a: string, b: string, t: number): [number, number, number] {
  const ah = hex(a);
  const bh = hex(b);
  return [
    Math.round(ah[0] + (bh[0] - ah[0]) * t),
    Math.round(ah[1] + (bh[1] - ah[1]) * t),
    Math.round(ah[2] + (bh[2] - ah[2]) * t),
  ];
}

function hex(color: string): [number, number, number] {
  const value = color.replace('#', '');
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
}

function resolveManifestRelative(catalog: StaticCatalog, productId: string, path: string): string {
  if (!path.startsWith('./')) return staticUrl(path);
  const manifestFile = catalog.products[productId]?.manifest_file ?? '';
  const base = manifestFile.split('/').slice(0, -1).join('/');
  return staticUrl(`${base}/${path.replace(/^\.\//, '')}`);
}
